import React, { useMemo } from 'react'
import styled from 'styled-components'
import type { IParsedBreaks } from 'features/breaks/breaksSlice'
import { convertMinsToHrsMins } from 'utils/helpers'
import { IScheduledBreaksModalState } from 'pages/Breaks/ScheduledBreaksModal'

type ScheduledBreak = IParsedBreaks['breaks'][number]

const minutesSinceMidnight = (isoDate: string): number => {
  const d = new Date(isoDate)
  return d.getHours() * 60 + d.getMinutes()
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const BreakBlock = styled.div<{ leftPct: number; widthPct: number; bg: string }>`
cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${(p) => p.leftPct}%;
  width: ${(p) => p.widthPct}%;
  height: 65%;
  min-width: 8px;
  box-sizing: border-box;
  background-color: #0F172A;
  border-radius: 6px;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.22);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.18);
  transition: 0.3s ease;

  ::hover{
  background-color: #575b64;
  outline:1px solid #ff9900;
  }

  .breakText {
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.1;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
    text-align: center;
    pointer-events: none;
  }
`

export const BreakInDayTimeline: React.FC<{ scheduledBreak: ScheduledBreak, onClick: (value: IScheduledBreaksModalState) => void, userId: number }> = ({
  scheduledBreak, onClick, userId
}) => {
  const blocks = useMemo(() => {
    const startMin = minutesSinceMidnight(scheduledBreak.scheduledStartTime)
    const endMinFromTime = minutesSinceMidnight(scheduledBreak.scheduledEndTime)
    const durationMin = Math.max(0, Number(scheduledBreak.breakDuration) || 0)

    // Compute an absolute end minute that can go beyond 24h,
    // so we can split across midnight when needed.
    const endMinAbsolute =
      endMinFromTime > startMin
        ? endMinFromTime
        : durationMin > 0
          ? startMin + durationMin
          : endMinFromTime + 1440

    const startClamped = clamp(startMin, 0, 1440)
    const endAbsoluteClamped = Math.max(startClamped, endMinAbsolute)

    const palette = ['#0F172A', '#334155', '#1D4ED8', '#0284C7', '#16A34A', '#A855F7']
    const colorIndex = (scheduledBreak.breakIndex ?? 0) % palette.length
    const bg = palette[colorIndex]

    const total = 1440
    const segments: Array<{ segStart: number; segEnd: number; key: string }> = []

    const firstEnd = Math.min(endAbsoluteClamped, total)
    if (firstEnd > startClamped) {
      segments.push({ segStart: startClamped, segEnd: firstEnd, key: 'first' })
    }

    if (endAbsoluteClamped > total) {
      const wrappedStart = 0
      const wrappedEnd = Math.min(endAbsoluteClamped - total, total)
      if (wrappedEnd > wrappedStart) {
        segments.push({ segStart: wrappedStart, segEnd: wrappedEnd, key: 'wrapped' })
      }
    }

    return segments.map((s) => {
      const leftPct = (s.segStart / total) * 100
      const widthPct = ((s.segEnd - s.segStart) / total) * 100

      const startLabel = convertMinsToHrsMins(s.segStart)
      const endLabel = convertMinsToHrsMins(s.segEnd)
      const label = `${startLabel}-${endLabel}`

      return { ...s, leftPct, widthPct, bg, label }
    })
  }, [scheduledBreak])

  if (!blocks.length) return null

  return (
    <>
      {blocks.map((b) => (
        <BreakBlock
          onClick={() => onClick({
            userId,
            breakIndex: scheduledBreak.breakIndex,
            newStartDate: scheduledBreak.scheduledStartTime
          })}
          key={`${scheduledBreak.breakIndex}-${scheduledBreak.scheduledStartTime}-${b.key}`}
          leftPct={b.leftPct}
          widthPct={b.widthPct}
          bg={b.bg}
          title={b.label}
        >
          <span className='breakText'>{b.label}</span>
        </BreakBlock>
      ))}
    </>
  )
}

